import React from "react";
import PropTypes from "prop-types";
import { Box, Stack, useMediaQuery } from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { DayPicker } from "react-day-picker";
import { format, isValid, parse, startOfDay } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar, Clock } from "lucide-react";
import "react-day-picker/dist/style.css";

import { Button } from "./Button";
import { CustomSelector } from "./CustomSelector";
import { getDayPickerSx } from "./dayPickerSx.js";

const DATE_VALUE_FORMAT = "yyyy-MM-dd";
const pad2 = (value) => String(value).padStart(2, "0");

/** "HH:mm" every `intervalMinutes`, from 00:00 to the last slot of the day. */
const buildTimeOptions = (intervalMinutes) => {
  const times = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += intervalMinutes) {
    times.push(`${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`);
  }
  return times;
};

/**
 * Single exact date (one-month calendar, same look as the date filters) plus a
 * time selector in `intervalMinutes` slots. Value is "yyyy-MM-ddTHH:mm" in local
 * time, the same format as a native datetime-local input.
 */
export const DateTimePickerField = ({
  value,
  onChange,
  disabled = false,
  disablePast = false,
  intervalMinutes = 15,
  defaultTime = "10:00",
}) => {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [datePart = "", timePart = ""] = (value || "").split("T");
  const selectedDate = React.useMemo(() => {
    if (!datePart) return undefined;
    const parsed = parse(datePart, DATE_VALUE_FORMAT, new Date());
    return isValid(parsed) ? parsed : undefined;
  }, [datePart]);
  const time = timePart.slice(0, 5);

  const timeOptions = React.useMemo(() => {
    const times = buildTimeOptions(intervalMinutes);
    // Keep an off-grid saved time (e.g. 17:51) selectable instead of losing it.
    if (time && !times.includes(time)) times.push(time);
    return times.sort().map((slot) => ({ value: slot, label: slot }));
  }, [intervalMinutes, time]);

  const emit = (nextDate, nextTime) => {
    if (!nextDate) {
      onChange("");
      return;
    }
    onChange(`${format(nextDate, DATE_VALUE_FORMAT)}T${nextTime || defaultTime}`);
  };

  const dateOptions = React.useMemo(
    () =>
      selectedDate
        ? [{ value: "__date__", label: format(selectedDate, "EEEE, d 'de' MMMM yyyy", { locale: es }) }]
        : [],
    [selectedDate]
  );

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) 132px" }, gap: 1 }}>
      <CustomSelector
        value={selectedDate ? "__date__" : null}
        onChange={() => {}}
        options={dateOptions}
        placeholder="Selecciona una fecha"
        leadingIcon={<Calendar size={16} />}
        disabled={disabled}
        menuWidth={isMobile ? 320 : 300}
        menuMaxHeight={400}
        menuEstimatedHeight={360}
        menuPadding={12}
        renderMenuContent={({ closeDropdown }) => (
          <Stack spacing={0.9}>
            <Box sx={getDayPickerSx(theme, { singleMonth: true })}>
              <DayPicker
                mode="single"
                locale={es}
                selected={selectedDate}
                onSelect={(date) => {
                  if (!date) return;
                  emit(date, time);
                  closeDropdown();
                }}
                numberOfMonths={1}
                defaultMonth={selectedDate || new Date()}
                disabled={disablePast ? { before: startOfDay(new Date()) } : undefined}
                fixedWeeks
              />
            </Box>
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="outline" onClick={closeDropdown}>
                Cerrar
              </Button>
            </Stack>
          </Stack>
        )}
      />
      <CustomSelector
        value={time || null}
        onChange={(nextTime) => emit(selectedDate, nextTime)}
        options={timeOptions}
        placeholder="Hora"
        leadingIcon={<Clock size={16} />}
        searchable
        disabled={disabled || !selectedDate}
      />
    </Box>
  );
};

DateTimePickerField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  disablePast: PropTypes.bool,
  intervalMinutes: PropTypes.number,
  defaultTime: PropTypes.string,
};
